import type queryString from 'query-string'

import { type UserProfile } from 'models'

import { type IPost } from '../types/interfaces/ITypes'

export const getImageOrientation = async (url: string): Promise<string> =>
    await new Promise((resolve, reject) => {
        const img = new Image()
        img.addEventListener('load', () => {
            let orientation = null
            if (img.naturalWidth > img.naturalHeight) {
                orientation = 'landscape'
            } else if (img.naturalWidth < img.naturalHeight) {
                orientation = 'portrait'
            } else {
                orientation = 'even'
            }
            resolve(orientation)
        })
        img.addEventListener('error', err => {
            reject(err)
        })
        img.src = url
    })

export function on<T extends Window | Document | HTMLElement | EventTarget>(
    obj: T | null,
    ...args:
        | Parameters<T['addEventListener']>
        | [string, Function | null, ...any]
): void {
    if (obj && obj.addEventListener) {
        obj.addEventListener(
            ...(args as Parameters<HTMLElement['addEventListener']>)
        )
    }
}

export function off<T extends Window | Document | HTMLElement | EventTarget>(
    obj: T | null,
    ...args:
        | Parameters<T['removeEventListener']>
        | [string, Function | null, ...any]
): void {
    if (obj && obj.removeEventListener) {
        obj.removeEventListener(
            ...(args as Parameters<HTMLElement['removeEventListener']>)
        )
    }
}

export function isDefined(value: any): boolean {
    return typeof value !== 'undefined'
}

export const randomString = (type: string) => {
    const ext = type.split('/')[1]
    const length = 12
    const name = Math.round(
        Math.pow(36, length + 1) - Math.random() * Math.pow(36, length)
    )
        .toString(36)
        .slice(1)
    return name + '.' + ext
}

export const validateMedia = async (mediaType: string, mediaFile: File) => {
    return await new Promise((resolve, reject) => {
        if (mediaType === 'image') {
            const url = URL.createObjectURL(mediaFile)
            const img = new Image()
            img.src = url
            img.onload = () => {
                if (img.width > 6000 || img.height > 6000) {
                    reject({ type: 'size', message: 'Image size is wrong!' })
                }

                resolve(true)
            }
        } else if (mediaType === 'video' || mediaType === 'audio') {
            const videoUploadLimit = 3221225472
            if (mediaFile.size > videoUploadLimit) {
                reject(Error('Media size is wrong!'))
            }
            resolve(true)
        }
    })
}

export const getRole = (userData: UserProfile, post: IPost) => {
    if (userData.role === 'model' && post.user_id === userData.id)
        return 'owner'
    return userData.role
}
export const validateText = (text: string) => {
    return text.trim().length !== 0 && text.trim().length < 150
}

export const toFixedNumber = (number: any, decimals = 2) => {
    return Number(number).toFixed(decimals)
}

export const queryFromSearchParams = (
    values: queryString.ParsedQuery<string>,
    initialQuery?: string
) => {
    if (initialQuery) {
        let query = initialQuery
        Object.entries(values).forEach(([key, value]) => {
            if (key === 'type') return
            if (!value || typeof value !== 'string') return

            query += '&' + key + '=' + value
        })
        return query
    } else {
        let query = '?'
        Object.entries(values).forEach(([key, value], idx) => {
            if (!value || typeof value !== 'string') return
            if (idx === 0) {
                query += key + '=' + value
            } else {
                query += '&' + key + '=' + value
            }
        })
        return query
    }
}

export function appendedArgsFn<
    TFn extends Function = (...args: any) => any,
    TAppendArgs extends unknown[] = any[]
>(fn: TFn, ...appendArgs: TAppendArgs) {
    return (...prependArgs: any) => fn(...prependArgs, ...appendArgs)
}
